import {
  createRootRouteWithContext,
  HeadContent,
  isRedirect,
  redirect,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { I18nProvider, RouterProvider } from "react-aria-components";
import type { ReactNode } from "react";
import { Toaster, UiLinkProvider } from "@beweegbaas/ui";
import { TanStackUiLink } from "#/components/ui-link-adapter";
import { PageShell } from "#/components/PageShell";
import { GlobalSpinner } from "#/components/global-spinner";
import { IntegreSqlBadge } from "#/components/integresql-badge";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { getRouteContext } from "#/modules/util.route-context/route-context.functions";
import type { RouteContext } from "#/modules/util.route-context/route-context.functions";
import { getPublicEnv } from "#/modules/util.instrumentation/public-env";
import { getThemeInitScript, ThemeProvider } from "#/modules/util.theme/theme";
import { cascadeLayersFallbackScript } from "#/polyfills/cascade-layers";

import appCss from "#/styles.css?url";

// Full auth state resolved once here (root beforeLoad) and read synchronously by the guards below.
// `queryClient` comes from the router context (created per request in `getRouter`), so loaders can
// prime the same cache the page components read.
export type RouterAppContext = RouteContext & { queryClient: QueryClient };

// `beforeLoad` re-runs on every hover-preload too, and the `preload` arg is always false for the
// root (it is an already-active match), so this cache is what keeps hovering a link from firing an
// auth RPC. Cleared by the router on every real load (router.tsx). Client-only.
let routeContext: RouteContext | null = null;
let routeContextPromise: Promise<RouteContext> | null = null;
const isClient = !import.meta.env.SSR;

export function clearRouteContextCache() {
  routeContext = null;
  routeContextPromise = null;
}

export const Route = createRootRouteWithContext<RouterAppContext>()({
  // Must not be `async`: a promise return marks the root `isFetching` and swaps the whole tree for
  // the pendingComponent on every preload.
  beforeLoad: (): RouteContext | Promise<RouteContext> => {
    if (!isClient) {
      return getRouteContext();
    }
    if (routeContext) {
      return routeContext;
    }
    // Single-flight: a navigation and any concurrent preloads share one in-flight RPC.
    routeContextPromise ??= getRouteContext().then((context) => {
      routeContext = context;
      routeContextPromise = null;
      return context;
    });
    return routeContextPromise.catch((error: Error) => {
      routeContextPromise = null;
      throw isRedirect(error) ? redirect(error.options) : error;
    });
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#1E43FF" },
      { property: "og:image", content: "/og-image.jpg" },
      { title: "Beweegbaas" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.json" },
      { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Dosis:wght@200..800&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Nunito:ital,wght@0,200..1000;1,200..1000&display=swap",
      },
    ],
  }),
  // Root is the only client-only route under defaultSsr: 'data-only', so the server renders
  // this pendingComponent as the single app-wide shell on initial load.
  pendingComponent: PageShell,
  // Never commit a still-pending match tree mid-navigation (default kicks in after 1s): a
  // suspended route without its own pendingComponent would blank out (e.g. changing the
  // number of groepjes in the les preparation). The previous page stays rendered — GlobalSpinner and
  // per-page PendingContent are the loading affordances. Initial document load is unaffected
  // (the shell renders via the ssr:false ClientOnly path, not this timeout).
  pendingMs: Infinity,
  errorComponent: ({ error }) => <ErrorBoundaryComponent error={error} showLogo />,
  shellComponent: RootDocument,
});

function Providers({ children }: { children: ReactNode }) {
  const router = useRouter();
  return (
    <I18nProvider locale="nl">
      <RouterProvider navigate={(to) => router.navigate({ href: to })} useHref={(href) => href}>
        <UiLinkProvider component={TanStackUiLink}>
          <ThemeProvider>
            {/* QueryClientProvider is installed by setupRouterSsrQueryIntegration (router.options.Wrap). */}
            <GlobalSpinner />
            <IntegreSqlBadge />
            {children}
          </ThemeProvider>
        </UiLinkProvider>
      </RouterProvider>
    </I18nProvider>
  );
}

function RootDocument({ children }: { children: ReactNode }) {
  // Classic (non-module) script, so it runs while the document is parsed — i.e. before the deferred
  // client bundle calls `getRouter()` and inits Sentry from `window.clientEnv`. On the client
  // `getPublicEnv()` reads that same object back, so the markup matches during hydration.
  const publicEnv = getPublicEnv();

  return (
    // suppressHydrationWarning: the theme script mutates the html class before React hydrates.
    <html lang="nl" suppressHydrationWarning>
      <head>
        <script
          dangerouslySetInnerHTML={{
            __html: `window.clientEnv = ${JSON.stringify(publicEnv)};`,
          }}
        />
        {/* Classic script, runs during parse: handles the ?dark unlock and sets `.dark` from
            the bb-theme cookie pre-paint. Gated inside the script (dev env or unlock cookie),
            so it is safe to inject everywhere. */}
        <script dangerouslySetInnerHTML={{ __html: getThemeInitScript() }} />
        <script dangerouslySetInnerHTML={{ __html: cascadeLayersFallbackScript }} />
        <HeadContent />
      </head>
      <body
        className="flex min-h-svh flex-col font-sans [overflow-wrap:anywhere] antialiased"
        // Portal defines this on its root wrapper (app/root.tsx). Without it, the side-drawer's
        // `top-[calc(var(--impersonating-header-height)+3px)]!` is invalid-at-computed-value-time and the
        // drawer collapses (renders as a short, top-anchored panel instead of a full-height side drawer).
        // 0px is portal's non-impersonating default; the impersonation banner is sticky-in-flow here.
        style={{ "--impersonating-header-height": "0px" }}
      >
        <Toaster />
        <Providers>{children}</Providers>
        <Scripts />
      </body>
    </html>
  );
}
